/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { api } from "../lib/api";
import { IApiKey, IServiceType } from "@/interfaces/Service.interface";
import { IArticle, ICustomActionAPIParams } from "@/interfaces/Analytics.interface";

/**
 * Project Types List
 *
 * @returns A Promise that resolves to an array of strings
 */
export const getServiceTypes = (): Promise<IServiceType[]> => {
  return api.get(`/service-types`, {
    withAuth: true,
  });
};


export const patchService = ({
  workspaceId,
  projectId,
  serviceKey,
  serviceData
}: {
  workspaceId: string;
  projectId: string;
  serviceKey: string;
  serviceData: Record<string, any>
}): Promise<string> => {
  return api.patch(`/workspaces/${workspaceId}/projects/${projectId}/services/${serviceKey}`, 
  { ...serviceData },
  {
    withAuth: true,
  });
};



/**
 * Trigger a custom action
 *
 * @param {object} params - The parameters for the POST request.
 * @param {string} params.workspaceId - The ID of the workspace to which the project belongs to.
 * @param {string} params.projectId - The ID of the project the API KEY belongs to.
 * @param {string} params.params - The params to send alongside the custom action.
 * @returns A Promise that resolves to a success message.
 */
export const triggerCustomAction = ({
  workspaceId,
  projectId,
  serviceKey,
  action,
  params,
}: {
  workspaceId: string;
  projectId: string;
  serviceKey: string;
  action: string;
  params: [] | null;
}): Promise<void> => {
  return api.post(`/workspaces/${workspaceId}/projects/${projectId}/services/${serviceKey}/custom-actions`, { action, params }, { withAuth: true });
};

/**
 * Fetch articles list for specific QnA entry
 *
 * @param {object} params - The parameters for the POST request.
 * @param {string} params.workspaceId - The ID of the workspace to which the project belongs to.
 * @param {string} params.projectId - The ID of the project to fetch analytics for.
 * @param {string} params.params - The custom action params.
 * @returns A Promise that resolves to a success message.
 */
export const getArticles = ({ workspaceId, projectId, actionParams }: ICustomActionAPIParams): Promise<IArticle[] | string> => {
  return api.post(
    `/workspaces/${workspaceId}/projects/${projectId}/services/cache/custom-actions`,
    { ...actionParams },
    { withAuth: true }
  );
};


/**
 * Create API KEY
 *
 * @param {object} params - The parameters for the DELETE request.
 * @param {string} params.workspaceId - The ID of the workspace to which the project belongs.
 * @param {string} params.projectId - The ID of the project the API KEY belongs to.
 * @param {string} params.serviceId - The ID of the service the API KEY belongs to.
 * @param {string} params.title - The title of the API KEY.
 * @returns A Promise that resolves to a success message.
 */
export const createApiKey = ({
  workspaceId,
  projectId,
  serviceKey,
  description,
}: {
  workspaceId: string;
  projectId: string;
  serviceKey: string;
  description: string;
}): Promise<IApiKey> => {
  return api.post(`/workspaces/${workspaceId}/projects/${projectId}/services/${serviceKey}/api-keys`, { description: description }, { withAuth: true });
};

/**
 * Delete API KEY
 *
 * @param {object} params - The parameters for the DELETE request.
 * @param {string} params.workspaceId - The ID of the workspace to which the project belongs to.
 * @param {string} params.projectId - The ID of the project the API KEY belongs to.
 * @param {string} params.serviceId - The ID of the service the API KEY belongs to.
 * @param {string} params.apiKeyId - The ID of the API KEY.
 * @returns A Promise that resolves to a success message.
 */
export const deleteApiKey = ({
  workspaceId,
  projectId,
  serviceKey,
  apiKeyId,
}: {
  workspaceId: string;
  projectId: string;
  serviceKey: string;
  apiKeyId: string;
}): Promise<IApiKey> => {
  return api.delete(`/workspaces/${workspaceId}/projects/${projectId}/services/${serviceKey}/api-keys/${apiKeyId}`, { withAuth: true });
};
